<template>
  <v-slide-y-transition data-test="coordinates-field-rem-row">
    <div v-if="showCoordinatesField">
      <v-row class="pb-2">
        <v-col class="grow pr-0">
          <vs-heading>Coordinates</vs-heading>
        </v-col>
        <v-col class="shrink">
          <div class="button-spacer">
            <vs-button
              data-test="remove-coordinates-selector"
              icon="clear"
              small
              destructive
              class="ml-1"
              type="tertiary"
              label="Add later"
              @click="$emit('remove-coordinates')"
            />
          </div>
        </v-col>
      </v-row>
      <div v-if="!isInMapView" class="map map-container">
        <field-map
          ref="coordinatesMiniMap"
          :center="initialCoordinatesMiniMapCenterPoint"
          :zoom="13"
          mini
          @move="$emit('on-minimap-moved', $event)"
        >
          <single-point-pin :data="latLonGeoJSONFormat" />
        </field-map>
      </div>
      <vs-wrapper direction="horizontal" space="small">
        <vs-text-input
          label="Latitude"
          :value="latitude"
          :rules="latitudeRules"
          type="number"
          number-step="0.0001"
          required
          class="inputCoordinate pr-2"
          data-test="latitude-field"
          @input="$emit('on-latitude-input', $event)"
        />
        <vs-text-input
          label="Longitude"
          :value="longitude"
          :rules="longitudeRules"
          type="number"
          number-step="0.0001"
          required
          class="inputCoordinate"
          data-test="longitude-field"
          @input="$emit('on-longitude-input', $event)"
        />
      </vs-wrapper>
    </div>
  </v-slide-y-transition>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { latLngToPoint, pointToMapData } from '@/utils/forms/point-helper'
import type GeoJSON from 'geojson'
import FieldMap from '@/components/field-view-next/mapbox/FieldMap.vue'
import SinglePointPin from '@/components/field-view-next/layers/SinglePointPin.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'

const latitudeRules = [
  (v: string) =>
    (parseFloat(v) < -90 || parseFloat(v) > 90) && 'Latitude must be between -90 and 90',
]
const longitudeRules = [
  (v: string) =>
    (parseFloat(v) < -180 || parseFloat(v) > 180) && 'Longitude must be between -180 and 180',
]
const props = defineProps<{
  showCoordinatesField?: boolean | null
  isInMapView?: boolean | null
  mapCenterPoint:
    | {
        latitude: number
        longitude: number
        zoom: number
      }
    | undefined
  latitude?: number | null
  longitude?: number | null
}>()

const coordinatesMiniMap = ref()

const initialCoordinatesMiniMapCenterPoint = computed(() => ({
  lat: props.mapCenterPoint?.latitude ?? 0,
  lng: props.mapCenterPoint?.longitude ?? 0,
}))
const latLonGeoJSONFormat = computed<GeoJSON.FeatureCollection<GeoJSON.Geometry>>(() => {
  if (
    props.latitude !== undefined &&
    props.latitude !== null &&
    props.longitude !== undefined &&
    props.longitude !== null
  ) {
    return pointToMapData(
      latLngToPoint({
        lat: props.latitude,
        lng: props.longitude,
      }),
    )
  }
  return pointToMapData(
    latLngToPoint({
      lat: 0,
      lng: 0,
    }),
  )
})

const jumpMinimapTo = (coordinates: { latitude: number; longitude: number }) => {
  coordinatesMiniMap.value.map.jumpTo({
    center: { lat: coordinates.latitude ?? 0, lng: coordinates.longitude ?? 0 },
  })
}

defineExpose({ jumpMinimapTo })
</script>

<style>
.map-container {
  overflow: auto;
  border-radius: var(--space-base);
}

.map {
  height: 320px;
  margin-block-end: 20px;
}

.inputCoordinate input[type='number'] {
  appearance: textfield;
}

.inputCoordinate input::-webkit-outer-spin-button,
.inputCoordinate input::-webkit-inner-spin-button {
  appearance: none;
}
</style>
